import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Database, Download, Trash2, RefreshCw, HardDrive, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BackupManagement = () => {
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      const res = await axios.get('/admin/backup/list');
      setBackups(res.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      toast.error('Fehler beim Laden der Backups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Möchten Sie jetzt ein vollständiges Datenbank-Backup erstellen?')) return;

    setCreating(true);
    try {
      const res = await axios.post('/admin/backup/create');
      
      if (res.data.success) {
        toast.success(
          `Backup erfolgreich erstellt! ${res.data.total_documents} Dokumente gesichert.`
        );
        loadBackups();
      } else {
        toast.error('Backup fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (timestamp) => {
    // Format: 20251209_144305 -> 09.12.2025, 14:43:05
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(9, 11);
    const minute = timestamp.substring(11, 13);
    const second = timestamp.substring(13, 15);
    
    return `${day}.${month}.${year}, ${hour}:${minute}:${second}`;
  };

  const formatSize = (docCount) => {
    // Rough estimate: 1 doc ≈ 2KB
    const sizeKB = docCount * 2;
    if (sizeKB < 1024) return `${sizeKB} KB`;
    return `${(sizeKB / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white flex items-center gap-3">
            <Database className="h-10 w-10" />
            Datenbank-Backups
          </h1>
          <p className="text-zinc-400 mt-2">
            Verwalten Sie Ihre Datenbank-Backups und Wiederherstellungen
          </p>
        </div>

        <Button
          onClick={handleCreateBackup}
          disabled={creating}
          size="lg"
          className="bg-blue-600 hover:bg-blue-700"
        >
          {creating ? (
            <>
              <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
              Erstelle Backup...
            </>
          ) : (
            <>
              <Download className="mr-2 h-5 w-5" />
              Neues Backup
            </>
          )}
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Gesamt Backups</p>
                <p className="text-2xl font-bold text-white">{backups.length}</p>
              </div>
              <HardDrive className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Letztes Backup</p>
                <p className="text-sm font-semibold text-white">
                  {backups.length > 0 ? formatDate(backups[0].timestamp).split(',')[0] : 'N/A'}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Dokumente (letztes)</p>
                <p className="text-2xl font-bold text-white">
                  {backups.length > 0 ? backups[0].total_documents || 0 : 0}
                </p>
              </div>
              <Database className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backup List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Verfügbare Backups</CardTitle>
          <CardDescription>
            Alle gespeicherten Datenbank-Backups. Backups werden automatisch im JSON-Format gespeichert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Noch keine Backups</h3>
              <p className="text-zinc-400 mb-4">Erstellen Sie Ihr erstes Backup</p>
              <Button onClick={handleCreateBackup} disabled={creating}>
                <Download className="mr-2 h-4 w-4" />
                Backup erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup, index) => (
                <div
                  key={backup.timestamp}
                  className={`p-4 rounded-lg border-2 ${
                    index === 0
                      ? 'bg-blue-900/20 border-blue-700'
                      : 'bg-zinc-800 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white">
                          Backup vom {formatDate(backup.timestamp)}
                        </h3>
                        {index === 0 && (
                          <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
                            Neuestes
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-sm text-zinc-400">
                        <div>
                          <span className="font-medium">Dokumente:</span>{' '}
                          <span className="text-white">{backup.total_documents || 0}</span>
                        </div>
                        <div>
                          <span className="font-medium">Collections:</span>{' '}
                          <span className="text-white">
                            {Object.keys(backup.collections || {}).length}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Größe:</span>{' '}
                          <span className="text-white">
                            {formatSize(backup.total_documents || 0)}
                          </span>
                        </div>
                      </div>

                      {backup.collections && (
                        <details className="mt-3">
                          <summary className="cursor-pointer text-sm text-blue-400 hover:text-blue-300">
                            Collections anzeigen ({Object.keys(backup.collections).length})
                          </summary>
                          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            {Object.entries(backup.collections).map(([name, count]) => (
                              <div key={name} className="bg-zinc-900/50 p-2 rounded">
                                <span className="text-zinc-400">{name}:</span>{' '}
                                <span className="text-white font-semibold">{count}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-white"
                        title="Backup-Informationen"
                      >
                        <Database className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-900/20 border-blue-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="h-5 w-5" />
            Backup-Informationen
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-zinc-300 space-y-2">
          <p>✅ Backups werden im JSON-Format gespeichert</p>
          <p>✅ Alle Collections werden automatisch gesichert</p>
          <p>✅ Backups sind unter <code className="text-blue-400">/app/backups/</code> gespeichert</p>
          <p>⚠️ Empfohlen: Erstellen Sie wöchentlich ein Backup</p>
          <p>💡 Tipp: Backups können auch automatisch geplant werden</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupManagement;
